import { useEffect, useRef, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
import {
  EventName,
  Request,
  useBridgeAPI,
  useBridgeState,
  useWebViewPostMessage,
} from '@digitaz/bridge-sdk-client';
import {
  Ad,
  AdKind,
  MutationError,
  PlatformSource,
} from '@/api/types/common.types';
import { AdService } from '@/api/types/services.types';
import {
  CreatePaymentResponse,
  PaymentMethod,
  PaymentStatus,
  TerminalPaymentTargetType,
} from '@/api/types/payments.types';
import { GetUserResponse } from '@/api/types/user.types';
import { GET_USER } from '@/api/queries/user.queries';
import {
  MY_AD_PROMOTION_STATUS_FIELDS,
  MyAdPromotionDetails,
  MyAdPromotionStatus,
} from '@/features/Profile/sections/MyAds/api';
import { getMyAdsPromotionPaymentUrl } from '@/features/Profile/sections/MyAds/components/MyAdsPromotionFlow/MyAdsPromotionFlow.helpers';
import { MyAdsPromotionServicesByKind } from '@/features/Profile/sections/MyAds/components/MyAdsPromotionFlow/MyAdsPromotionFlow.types';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useNewWindow } from '@/hooks/useNewWindow';
import { useCreatePayment } from '@/hooks/useCreatePayment';
import { useTerminalPayment } from '@/components/Payment/useTerminalPayment';
import { usePostMessage } from '@/hooks/usePostMessage';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { useAuthStore } from '@/store/useAuthStore';
import { usePaymentStatusModalStore } from '@/store/usePaymentStatusModalStore';
import { PaymentStatusParams } from '@/bridge/types/payment';
import {
  buildDataAndOpenWebViewPaymentModal,
  buildServiceId,
  canApplyWalletDiscount,
  getPromotionsData,
} from '@/components/Ad/Promotions/PromotionFlow.helpers';
import { formatDate, getLoginUrlWithReturn, sleep } from '@/utils/helpers';
import { buildHashStringForTerminal } from '@/components/Payment/PaymentInTerminals.helpers';
import {
  getPaymentYigimTemplateGroup,
  getRefetchDelay,
} from '@/api/helpers/payments.helpers';
import { DeviceType, getDeviceType } from '@/utils/getDeviceType';

type Props = {
  ad: Ad | null;
  adDetails?: MyAdPromotionDetails | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccessPayment?: () => Promise<void> | void;
  services: MyAdsPromotionServicesByKind;
  source: PlatformSource;
};

export function useMyAdsPromotionPayment({
  adDetails,
  isOpen,
  onClose,
  onSuccessPayment,
  services,
  source,
}: Props) {
  const userAgent = window.navigator.userAgent || '';
  const { deviceType } = getDeviceType(userAgent);
  const isDesktopDevice = deviceType === DeviceType.Desktop;
  const { t, i18n } = useTranslation();
  const client = useApolloClient();
  const router = useRouter();
  const customPush = useCustomPush();
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  const shouldSkipNextPaymentFinished = useRef(false);
  const [selectedType, setSelectedType] = useState<AdKind | null>(null);
  const [service, setService] = useState<AdService | null>(null);
  const [payment, setPayment] = useState(PaymentMethod.My_card);
  const [cardId, setCardId] = useState<string | undefined>(undefined);
  const [createPaymentError, setCreatePaymentError] = useState('');
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [isNotEnoughFundsOpen, toggleNotEnoughFunds] = useToggleOpenState();
  const [isAddBalanceOpen, toggleAddBalance] = useToggleOpenState();
  const [isTooManyRequestsOpen, toggleTooManyRequests] = useToggleOpenState();
  const [
    openWebViewPaymentModal,
    setIsSuccessOpen,
    setSuccessPaymentModalData,
  ] = usePaymentStatusModalStore(state => [
    state.openWebViewPaymentModal,
    state.setIsSuccessPaymentModalOpen,
    state.setSuccessPaymentModalData,
  ]);
  const {
    newWindow: paymentWindow,
    close: closePaymentWindow,
    open: openPaymentWindow,
  } = useNewWindow();
  const { isWebView } = useBridgeState();
  const bridgeAPI = useBridgeAPI();
  const { data: userPaymentInfo, refetch: refetchUserPaymentInfo } =
    useQuery<GetUserResponse>(GET_USER, { errorPolicy: 'all' });
  const {
    createPayment,
    loading: isPaymentLoading,
    paymentProvider,
  } = useCreatePayment({
    onSuccess: onCreatePaymentSuccess,
    onError: onCreatePaymentError,
    onRedirect: onCreatePaymentRedirect,
  });
  const {
    isTerminalOpen,
    toggleTerminal,
    isTerminal,
    createTerminalPayment,
    isCreateTerminalPaymentLoading,
  } = useTerminalPayment({ payment });

  const walletDiscount = userPaymentInfo?.currentUser?.walletDiscount ?? 0;
  const isAppleGooglePayEnabled = Boolean(
    userPaymentInfo?.currentUser?.isAppleGooglePayEnabled
  );
  const paymentMethods = userPaymentInfo?.currentUser?.paymentMethods;
  const applyWalletDiscount =
    canApplyWalletDiscount(payment) && walletDiscount > 0;
  const isWallet = payment === PaymentMethod.Wallet;
  const isMyCard = payment === PaymentMethod.My_card;
  const isPaymentFlowOpen = isOpen && !!selectedType;

  function closeFlow() {
    setSelectedType(null);
    setService(null);
    onClose();
  }

  function handleSelectPromotion(type: AdKind) {
    setSelectedType(type);
  }

  function updateSuccessPaymentModalData(
    details: {
      featuredUntil?: string | null;
      vippedUntil?: string | null;
    },
    type: AdKind
  ) {
    setSuccessPaymentModalData(
      getPromotionsData({
        t,
        isSuccessPopup: true,
        ...(details.vippedUntil && {
          vippedUntil: formatDate(details.vippedUntil),
        }),
        ...(details.featuredUntil && {
          featuredUntil: formatDate(details.featuredUntil),
        }),
      })[type]
    );
  }

  function refreshSuccessPaymentModalData({
    paidType,
    refetchPromise,
    shouldOpenSuccessModal = false,
  }: {
    paidType: AdKind | null;
    refetchPromise: Promise<unknown>;
    shouldOpenSuccessModal?: boolean;
  }) {
    const refresh = Promise.allSettled([refetchPromise]);
    if (!paidType || !adDetails) return;

    refresh.then(() => {
      const promotionStatus = client.readFragment<MyAdPromotionStatus>({
        fragment: MY_AD_PROMOTION_STATUS_FIELDS,
        id: client.cache.identify({ __typename: 'Ad', id: adDetails.id }),
      });

      if (!promotionStatus) return;

      updateSuccessPaymentModalData(promotionStatus, paidType);

      if (shouldOpenSuccessModal) {
        setIsSuccessOpen(true);
      }
    });
  }

  function handlePaymentSuccess() {
    const paidType = selectedType;
    const paidService = service;
    const refetchPromise = Promise.resolve(onSuccessPayment?.());

    closeFlow();

    refreshSuccessPaymentModalData({
      paidType,
      refetchPromise,
      shouldOpenSuccessModal: !!paidService,
    });

    if ([PaymentMethod.Wallet, PaymentMethod.Card].includes(payment)) {
      refetchUserPaymentInfo();
    }
  }

  function onCreatePaymentError(
    status: PaymentStatus,
    errors?: MutationError[]
  ) {
    closePaymentWindow();

    const notEnoughFunds = errors?.some(el => el.code === 'insufficient_funds');
    if (notEnoughFunds) {
      toggleNotEnoughFunds(true);
      return;
    }

    const tooManyRequests = errors?.some(el => el.code === '429');
    if (tooManyRequests) {
      toggleTooManyRequests(true);
      return;
    }

    if (errors?.length && status === PaymentStatus.Failure) {
      const duplicated = errors[0].code === '666';
      setIsDuplicate(duplicated);

      if (isWebView) {
        if (duplicated) {
          closeFlow();
          openWebViewPaymentModal({ status: PaymentStatus.Duplicate });
        } else {
          openWebViewPaymentModal({ status: PaymentStatus.Failure });
        }
        return;
      }

      setCreatePaymentError(errors[0].message);
      if (duplicated) {
        closeFlow();
      }
    }
  }

  function onCreatePaymentRedirect(redirectUrl: string) {
    if (isWebView) {
      bridgeAPI.openPayment(redirectUrl);
      return;
    }

    if (paymentWindow) {
      paymentWindow.location.href = redirectUrl;
    }
  }

  function onCreatePaymentSuccess(data: CreatePaymentResponse) {
    const url = getMyAdsPromotionPaymentUrl({
      data,
      adDetails,
      service,
      type: selectedType,
      walletDiscount,
      applyWalletDiscount,
      payment,
    });

    if (isWebView) {
      const refetchPromise = Promise.resolve(onSuccessPayment?.());

      closeFlow();
      refreshSuccessPaymentModalData({
        paidType: selectedType,
        refetchPromise,
      });
      if ([PaymentMethod.Wallet, PaymentMethod.Card].includes(payment)) {
        refetchUserPaymentInfo();
      }
      buildDataAndOpenWebViewPaymentModal({
        i18n,
        url,
        data,
        service,
        type: selectedType,
        bundle: null,
        isBundles: false,
        isWallet,
        openWebViewPaymentModal,
      });
      return;
    }

    handlePaymentSuccess();

    if (url && paymentWindow) {
      paymentWindow.location.href = url;
      return;
    }

    closePaymentWindow();
  }

  function payWithTerminal() {
    if (!adDetails) return;

    createTerminalPayment({
      variables: {
        serviceId: buildServiceId(service, selectedType, null, false),
        targetId: adDetails.id,
      },
    });
  }

  function payWithNonTerminalMethods() {
    if (!adDetails) return;

    shouldSkipNextPaymentFinished.current = false;
    const walletBalance = userPaymentInfo?.currentUser?.walletBalance;

    createPayment({
      variables: {
        serviceId: buildServiceId(service, selectedType, null, false),
        targetId: adDetails.id,
        paymentMethod: payment,
        yigimTemplateGroup: getPaymentYigimTemplateGroup(
          isDesktopDevice,
          isAppleGooglePayEnabled
        ),
        source,
        ...(isMyCard && cardId && { cardId }),
      },
    });

    if (payment === PaymentMethod.Wallet && !walletBalance) return;

    if (!isWebView) {
      openPaymentWindow();
    }
  }

  function handlePayClick() {
    if (!isAuthenticated && payment === PaymentMethod.Terminal) {
      const cleanPath = router.asPath.split('#')[0];

      customPush(
        getLoginUrlWithReturn(
          `${cleanPath}#${buildHashStringForTerminal(
            service,
            selectedType,
            TerminalPaymentTargetType.Ad
          )}`,
          cleanPath
        )
      );
      return;
    }

    if (isTerminal) {
      payWithTerminal();
      return;
    }

    payWithNonTerminalMethods();
  }

  function clearCreatePaymentError() {
    setCreatePaymentError('');
  }

  function startAddBalance() {
    toggleNotEnoughFunds(false);
    toggleAddBalance(true);
  }

  function handleCloseTooManyRequestsDrawer() {
    toggleTooManyRequests(false);
    closeFlow();
  }

  function handleWebViewPostMessage(
    params: Request.Params[EventName.PaymentStatus]
  ) {
    if (!isPaymentFlowOpen || isAddBalanceOpen || isMyCard) return;

    const delay = getRefetchDelay(paymentProvider);
    const paymentParams = params as unknown as PaymentStatusParams;

    openWebViewPaymentModal(paymentParams);

    if (paymentParams.status === PaymentStatus.Success) {
      closeFlow();

      sleep(delay).then(() => {
        const refetchPromise = Promise.resolve(onSuccessPayment?.());

        refreshSuccessPaymentModalData({
          paidType: selectedType,
          refetchPromise,
        });
        if ([PaymentMethod.Wallet, PaymentMethod.Card].includes(payment)) {
          refetchUserPaymentInfo();
        }
      });
    }
  }

  function handlePostMessage() {
    if (shouldSkipNextPaymentFinished.current) {
      shouldSkipNextPaymentFinished.current = false;
      return;
    }

    if (!isPaymentFlowOpen || isAddBalanceOpen || isMyCard) return;

    const delay = getRefetchDelay(paymentProvider);

    sleep(delay).then(handlePaymentSuccess);
  }

  useEffect(() => {
    if (!isOpen) {
      setSelectedType(null);
      setService(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!selectedType || !adDetails) return;

    updateSuccessPaymentModalData(adDetails, selectedType);

    const currentServices = services[selectedType];
    if (!currentServices?.length) return;

    if (currentServices.length > 2) {
      setService(currentServices[currentServices.length - 2]);
      return;
    }

    setService(currentServices[1] ?? currentServices[0]);
  }, [
    selectedType,
    adDetails?.vippedUntil,
    adDetails?.featuredUntil,
    adDetails?.bumpServices,
    adDetails?.featuredServices,
    adDetails?.vipServices,
  ]);

  useWebViewPostMessage({
    eventName: EventName.PaymentStatus,
    handler: handleWebViewPostMessage,
  });

  usePostMessage({
    postMessageName: 'paymentFinished',
    handler: handlePostMessage,
  });

  return {
    cardId,
    clearCreatePaymentError,
    closeFlow,
    createPaymentError,
    handleCloseTooManyRequestsDrawer,
    handlePayClick,
    handleSelectPromotion,
    isAddBalanceOpen,
    isAppleGooglePayEnabled,
    isCreateTerminalPaymentLoading,
    isDuplicate,
    isNotEnoughFundsOpen,
    isPaymentFlowOpen,
    isPaymentLoading,
    isTerminalOpen,
    isTooManyRequestsOpen,
    payment,
    paymentMethods,
    selectedType,
    service,
    setCardId,
    setPayment,
    setService,
    shouldSkipNextPaymentFinished,
    startAddBalance,
    toggleAddBalance,
    toggleNotEnoughFunds,
    toggleTerminal,
    walletDiscount,
  };
}
